import { apiClient } from './apiClient';

export interface Preset {
  id: number;
  preset_name: string;
  description?: string;
  content_type: string;
  target_length: number;
  tone: string;
  target_audience: string;
  emoji_usage: string;
  usage_count: number;
  is_favorite: boolean;
  tags: string[];
  created_at: string;
  last_used_at?: string;
  blog_options?: BlogOptions; // 추가: 호환성을 위한 선택적 속성
  personality_id?: number; // 추가: 호환성을 위한 선택적 속성
  priority?: 'quality' | 'speed' | 'balance'; // 추가: 호환성을 위한 선택적 속성
}

export interface BlogOptions {
  contentType: 'informational' | 'commercial' | 'review' | 'tutorial';
  targetLength: number;
  tone: 'professional' | 'casual' | 'friendly';
  targetAudience: 'beginner' | 'intermediate' | 'expert';
  language: 'ko' | 'en';
  emojiUsage: 'none' | 'minimal' | 'moderate' | 'heavy';
  emoticonsAllowed: boolean;
  visualExpression: 'text-only' | 'emoji-accent' | 'emoji-rich';
  selectedCombination?: {
    personalityType?: string;
    writingStyle?: string;
    designTheme?: string;
    contentStructure?: string;
    audienceRelation?: string;
  };
}

export interface SavePresetRequest {
  preset_name: string;
  description?: string;
  blog_options: BlogOptions;
  selected_combination?: any;
  tags?: string[];
  user_id?: number; // 추가: 호환성을 위한 선택적 속성
}

export class PresetService {
  /**
   * 프리셋 저장
   */
  async save(request: SavePresetRequest): Promise<{ success: boolean; preset_id?: number }> {
    try {
      const response = await apiClient.post<{ success: boolean; preset_id: number; message: string }>(
        '/v2/presets/save.php',
        request
      );

      if (response.data.success) {
        return { success: true, preset_id: response.data.preset_id };
      } else {
        throw new Error('프리셋 저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('프리셋 저장 오류:', error);
      throw new Error(error.response?.data?.error || '프리셋 저장에 실패했습니다.');
    }
  }

  /**
   * 프리셋 목록 조회
   */
  async list(): Promise<Preset[]> {
    try {
      const response = await apiClient.get<{ success: boolean; presets: Preset[] }>(
        '/v2/presets/list.php'
      );

      return response.data.success ? response.data.presets : [];
    } catch (error: any) {
      // 401 오류는 apiClient의 interceptor에서 처리됨
      // 여기서는 추가 로깅만 수행
      if (error.response?.status === 401) {
        // 인터셉터에서 이미 리다이렉트 처리됨
        return [];
      }
      console.error('프리셋 목록 조회 오류:', error);
      return [];
    }
  }

  /**
   * 프리셋 상세 조회
   */
  async getById(presetId: number): Promise<Preset | null> {
    try {
      const response = await apiClient.get<{ success: boolean; preset: Preset }>(
        `/v2/presets/get.php?id=${presetId}`
      );

      return response.data.success ? response.data.preset : null;
    } catch (error: any) {
      // 401 오류는 apiClient의 interceptor에서 처리됨
      if (error.response?.status === 401) {
        return null;
      }
      console.error('프리셋 상세 조회 오류:', error);
      return null;
    }
  }

  /**
   * 프리셋 사용 (사용 횟수 증가)
   */
  async use(presetId: number): Promise<boolean> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/v2/presets/use.php',
        { preset_id: presetId }
      );

      return response.data.success;
    } catch (error: any) {
      // 401 오류는 apiClient의 interceptor에서 처리됨
      if (error.response?.status === 401) {
        return false;
      }
      console.error('프리셋 사용 기록 오류:', error);
      return false;
    }
  }
}

export const presetService = new PresetService();

